/**
 * A link from before projects (`/experiments/123`) or a mistyped path at the
 * root: reopen it in the last project used on this browser. When that one is
 * gone, the next project `/` would open; with none remembered, your only
 * project. A project runs in its own router, so that is a page load. Anything
 * else goes to `/`, and a signed-out visitor signs in first, keeping the link.
 */

import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { ApiError } from "@/lib/api";
import { lastProject, projectHref } from "@/lib/project-url";
import { useProjects } from "./hooks";
import { pickProject } from "./open-last-project";
import { LoadingScreen, ProjectNotFound } from "./project-gate";
import type { Project } from "./types";

/** Which project an old link opens in, or null to leave it to `/`. */
export function legacyProject(projects: Project[], last: string | null): Project | null {
  if (last) return pickProject(projects, last);
  return projects.length === 1 ? projects[0]! : null;
}

export function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  // `/p/<bad%encoding>` names a project nobody can open; `/p` alone names none.
  const malformed = pathname.startsWith("/p/");
  const bare = /^\/p\/?$/.test(pathname);
  const projects = useProjects(!malformed && !bare);
  const project = projects.data ? legacyProject(projects.data, lastProject()) : null;
  const target = project ? projectHref(project.slug, `${pathname}${search}${hash}`) : null;

  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);

  if (malformed) return <ProjectNotFound />;
  if (projects.error instanceof ApiError && projects.error.status === 401)
    return <Navigate to={`/login?next=${encodeURIComponent(`${pathname}${search}${hash}`)}`} replace />;
  if (bare || projects.isError || (projects.isSuccess && !target)) return <Navigate to="/" replace />;
  return <LoadingScreen label="Opening your project" />;
}
